
import { useEffect } from 'react';

import { connect, useSelector } from 'react-redux';
import { Redirect, Route, RouteComponentProps, Switch, useLocation, withRouter } from 'react-router-dom';

import { isHomeFromPath, RootState, VACATIONS_PAGE } from '@share/utils';
import {
  ChangePassword,
  ForgotPassword,
  GenericNotFoundPage,
  NotAuthorizedPage,
  PrivateRoute,
  ResetPassword,
  SendVerification,
  SignIn,
  SignUp,
  VerifyUser,
} from '@components';
import { ILoginState, IMenuState, refreshProfile } from '@share/store/slices';
import { IPageType, Routes } from '@share/constants';

import { SearchPage } from '../search';
import { CondoPage } from '../condo';
import { ReservationsPage } from '../reservations';
import { AccountPage } from '../account';
import { ThreeDS } from '../three-ds';
import { CarsPage } from '../cars';

import { HotelDetailPage } from '../hotel-details';
import { CondoDetailPage } from '../condo-details';
import { SubmitRequestPage } from '../all-inclusives/submit-request';
import { CondoReviewBookPage } from '../condo-review-book';
import { ReservationCancellation } from '../reservation-cancellation';
import { CarsDetailPage } from '../cars-details';

import { WeeksDetailsPage, WeeksSearchPage } from '../weeks';
import { WeeksReviewBookPage } from '../weeks/weeks-review-book';

import ReviewBookPage from '../review-book/component';
import { NULL_VALUE } from '@constants';
import { StaticDetailPage } from '../static-details';
import { Home } from '../../pages/new-home';
import Login from '../../pages/log-in/login';
import Register from '../../pages/register/register-company';
import AccountSettings from '../../pages/account-settings/account-settings';
import Notifications from '../notification/notification';
import { PaymentDetails } from '../payment-details';
import Bookings from '../../pages/bookings/component';
import AccountSaved from '../../pages/account-saved/account-saved';
import { Dashboard } from '../dashboard';
import CompanySettings from '../../pages/company-settings/component';
import CompanyTeam from '../../pages/company-team/component';
import TwoFactorAuthentication from '../../pages/two-factor-authentication/component';
import { CustomErrorPage } from '@share/components';
import AccountReward from '../account-reward/account-reward';
import CompanyIntegrations from '../../components/company-integrations/component';
import DashboardHotelBook from '../dashboard-hotel-book/component';
import CompanyPreferences from '../../components/company-preferences/component';
import CompanyBranding from '../../components/company-branding/component';
import CompanyTrends from '../../components/company-trends/component';
import CompanyTravel from '../../components/company-travel/component';

import { PrivateRouteNewApp } from '../../components/common/private-route';
import { ResetPasswordNew } from '../reset-password-new';
import CompanyCreditLine from '../../components/company-credit-line/component';
// import CompanyStatement from '../../components/company-statement/component';
import CompanyCustomFields from '../../components/company-custom-fields/component';
// import CompanyMerge from '../../components/company-merge/component';
import { InsuranceSearchPage } from '../insurance';
import PersonalInformation from '../account-settings/personal-information';
import React from 'react';
import { AdminTravBusiness, HasPermission } from '@utils';
import TravBusinessSolutionsTravelManagersPage from '../travbusiness-solutions-for-travel-managers/component';
import TravbusinessSolutionsForOperationsManagersPage from '../travbusiness-solutions-for-operations-managers/component';
import TravbusinessYourSolutionForEasyTravelInvoicesPage from '../travbusiness-your-solution-for-easy-travel-invoices/component';
import TravBusinessReportingInsightsPage from '../travbusiness-reporting-insights/component';
import TravBusinessPrivacyPolicyPage from '../travbusiness-privacy-policy/component';
import TravBusinessTermsOfUsePage from '../travbusiness-terms-of-use/component';
import TravBusinessAboutUsHistoryTravelPage from '../travbusiness-about-us-history-travel/component';
import TravbusinesLoyaltyRewardsBenefitsForTravelersHotelAndCarPage from '../travbusiness-loyalty-rewards-benefits-for-travelers-hotel-and-car/component';
import TravbusinessSolutionsForFinanceManagersPage from '../travbusiness-solutions-for-finance-managers/component';
import TravbusinessSolutionsForTravelersHotelsCarsPage from '../travbusiness-solutions-for-travelers-hotels-cars/component';
import CompanyDepartment from '../company-department/component';
import { CompanyPaymentDetails } from '../../components/company-payment-details';
import CompanyBookings from '../../components/company-bookings/component';
import TravBusinessFaqPage from '../travbusiness-faq/component';
import TravBusinessProgrammeWorksPage from '../travbusiness-programme-works/component';

interface IMapStateToProps {
  menuStore: IMenuState;
}

interface IMapDispatchToProps {
  refreshProfile: (pageType: IPageType) => void;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps, RouteComponentProps {
}

function ApplicationRoutesComponent(props: IProps) {

  const { menuStore, history, refreshProfile } = props;
  const { items } = menuStore;

  const location = useLocation();

  const loactionPath = location?.pathname;

  const refresh = () => {
    if (!!items) {
      refreshProfile(isHomeFromPath(history) ? IPageType.Home : NULL_VALUE);
    }
  }

  useEffect(() => refresh(), [loactionPath]);
  useEffect(() => refresh(), [items?.profile]);

  const loginStore = useSelector((state: { loginStore: ILoginState }) => state.loginStore);
  const loggedUser = loginStore.user;

  const publicRoutes = [
    { path: '/:accountUsername/log-in', component: Login },
    { path: '/:accountUsername/two-factor-authentication', component: TwoFactorAuthentication },
    { path: '/:accountUsername/reset-password/:token', component: ResetPasswordNew },
    { path: '/:accountUsername/register', component: Register },
    { path: '/:accountUsername/', component: Home },
  ];

  const privateRoutes = [
    { path: '/:accountUsername/dashboard', component: Dashboard },

    { path: '/:accountUsername/hotel', component: Dashboard },

    { path: `/:accountUsername${Routes.CarsDashboard}`, component: CarsPage },
    { path: `/:accountUsername${Routes.CarsSearch}`, component: CarsPage },
    { path: `/:accountUsername${Routes.CarsDetails}/:id`, component: CarsDetailPage },
    { path: `/:accountUsername${Routes.InsuranceQuote}`, component: InsuranceSearchPage },
    { path: `/:accountUsername/travbusiness-solutions-for-travel-managers`, component: TravBusinessSolutionsTravelManagersPage },
    { path: `/:accountUsername/travbusiness-solutions-for-operations-managers`, component: TravbusinessSolutionsForOperationsManagersPage },
    { path: `/:accountUsername/travbusiness-solutions-for-finance-managers`, component: TravbusinessSolutionsForFinanceManagersPage },
    { path: `/:accountUsername/travbusiness-solutions-for-travelers-hotels-cars`, component: TravbusinessSolutionsForTravelersHotelsCarsPage },
    { path: `/:accountUsername/travbusiness-loyalty-rewards-benefits-for-travelers-hotel-and-car`, component: TravbusinesLoyaltyRewardsBenefitsForTravelersHotelAndCarPage },
    { path: `/:accountUsername/travbusiness-your-solution-for-easy-travel-invoices`, component: TravbusinessYourSolutionForEasyTravelInvoicesPage },
    { path: `/:accountUsername/travbusiness-reporting-insights`, component: TravBusinessReportingInsightsPage },
    { path: `/:accountUsername/travbusiness-privacy-policy`, component: TravBusinessPrivacyPolicyPage },
    { path: `/:accountUsername/travbusiness-terms-of-use`, component: TravBusinessTermsOfUsePage },
    { path: `/:accountUsername/travbusiness-about-us-history-travel`, component: TravBusinessAboutUsHistoryTravelPage },
    { path: `/:accountUsername/travbusiness-faq`, component: TravBusinessFaqPage },
    { path: `/:accountUsername/travbusiness-how-program-works`, component: TravBusinessProgrammeWorksPage },

    { path: '/:accountUsername/personal-information', component: PersonalInformation },
    { path: '/:accountUsername/account-settings', component: AccountSettings },
    { path: '/:accountUsername/account-payment', component: PaymentDetails },
    { path: '/:accountUsername/company-account-payment', component: CompanyPaymentDetails },
    { path: '/:accountUsername/bookings', component: Bookings },
    { path: '/:accountUsername/company-bookings', component: CompanyBookings },
    { path: '/:accountUsername/account-rewards', component: AccountReward },
    // { path: '/:accountUsername/account-credit', component: AccountCredit },
    { path: '/:accountUsername/account-saved', component: AccountSaved },
    { path: '/:accountUsername/hotel-book/:hotelId/:uuid', component: DashboardHotelBook },
    { path: '/:accountUsername/company-settings', component: CompanySettings },
    { path: '/:accountUsername/company-team', component: CompanyTeam },
    { path: '/:accountUsername/company-department', component: CompanyDepartment },
    { path: '/:accountUsername/company-integrations', component: CompanyIntegrations },
    { path: '/:accountUsername/company-preferences', component: CompanyPreferences },
    { path: '/:accountUsername/company-branding', component: CompanyBranding },
    { path: '/:accountUsername/company-custom-fields', component: CompanyCustomFields },
    // { path: '/:accountUsername/company-merge', component: CompanyMerge },
    { path: '/:accountUsername/company-trends', component: CompanyTrends },
    { path: '/:accountUsername/company-travel', component: CompanyTravel },
    { path: '/:accountUsername/credit-line', component: CompanyCreditLine },
    // { path: '/:accountUsername/statement-overview', component: CompanyStatement },
    { path: '/:accountUsername/notifications-list', component: Notifications },
    { path: `/:accountUsername/notifications-list/:id`, component: Notifications },
  ];

  return (
    <Switch>

      <PrivateRoute path={`/:accountUsername${Routes.Reservations}`} exact={true}>
        <ReservationsPage />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.RsiAccount}`}>
        <AccountPage key="account" />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.AccountReservations}`}>
        <AccountPage key="account_reservation" />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.RsiTestDriveManager}`}>
        <AccountPage key="account_test_drive" />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.RsiQuotes}/:id`}>
        <AccountPage key="account_my_qoutes" />
      </PrivateRoute>

      <PrivateRoute path={`/:accountUsername${Routes.CondoSearch}`}>
        <CondoPage />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.CondosDashboard}`}>
        <CondoPage />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.Condo}/:id`}>
        <CondoDetailPage />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.UnitBook}/:id`}>
        <CondoReviewBookPage />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.CondoSearch}/submit-request`}>
        <SubmitRequestPage isCondoPage />
      </PrivateRoute>

      <PrivateRoute path={`/:accountUsername${Routes.Search}`} exact={true}>
        <SearchPage key="hotels" />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.Hotel}/:id`} exact={true}>
        <HotelDetailPage key="hotels" />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.Hotel}/:id/:name`} exact={true}>
        <StaticDetailPage key="static-hotel" type='hotel' />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.Book}/:id/:packageId`} exact={true}>
        <ReviewBookPage key="hotels" />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.RequestCancelReservation}/:id`}>
        <ReservationCancellation />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.ReservationCancellation}/:id`}>
        <ReservationCancellation />
      </PrivateRoute>

      <PrivateRoute path={`/:accountUsername${Routes.Search}/${VACATIONS_PAGE}`} exact={true}>
        <SearchPage isVacationRentals={true} key="vacations" />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.Hotel}/:id/${VACATIONS_PAGE}`} exact={true}>
        <HotelDetailPage isVacationRentals={true} key="vacations" />
      </PrivateRoute>
      <PrivateRoute
        path={`/:accountUsername${Routes.Book}/:id/:packageId/${VACATIONS_PAGE}`}
        exact={true}
      >
        <ReviewBookPage isVacationRentals={true} key="vacations" />
      </PrivateRoute>

      <Route path={`/:accountUsername${Routes.ThreeDS}`} exact={true}>
        <ThreeDS />
      </Route>

      <Route path={`/:accountUsername${Routes.Login}`} exact={true}>
        <SignIn />
      </Route>
      <Route path={`/:accountUsername${Routes.Login}/:bookingID`} exact={true}>
        <SignIn />
      </Route>
      <Route path={`/:accountUsername${Routes.SignUp}`} exact={true}>
        <SignUp />
      </Route>
      <Route path={`/:accountUsername${Routes.SignUp}/:bookingID`} exact={true}>
        <SignUp />
      </Route>

      <Route path={`/:accountUsername${Routes.VerifyUser}/:token`} exact={true}>
        <VerifyUser />
      </Route>

      <Route path={`/:accountUsername${Routes.SendVerifyEmail}`} exact={true}>
        <SendVerification />
      </Route>
      <Route path={`/:accountUsername${Routes.ForgotPassword}`} exact={true}>
        <ForgotPassword />
      </Route>
      <Route path={`/:accountUsername${Routes.ResetPassword}/:token`} exact={true}>
        <ResetPassword />
      </Route>
      <Route path={`/:accountUsername${Routes.ChangePassword}`} exact={true}>
        <ChangePassword />
      </Route>

      <PrivateRoute path={`/:accountUsername${Routes.WeeksSearch}`}>
        <WeeksSearchPage />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.WeeksDetails}/:id`}>
        <WeeksDetailsPage />
      </PrivateRoute>
      <PrivateRoute path={`/:accountUsername${Routes.WeeksBook}/:id/:packageId`}>
        <WeeksReviewBookPage />
      </PrivateRoute>





      {publicRoutes.map(({ path, component }) => (
        <Route key={path} path={path} exact component={component} />))}

      {privateRoutes.map(({ path, component }) => (
        <PrivateRouteNewApp
          key={path}
          path={path}
          exact
          component={component}
          userData={loggedUser}
        />
      ))}

      {/*privateRoutes.map(({ path, component }) => (
        <PrivateRouteNewApp
          key={path}
          path={path}
          exact
          component={(props: any) => {
            if (!ISADMIN && (travelerUnauthorizedPaths.includes(path) || coordinatorUnauthorizedPaths.includes(path))) {
              return <Redirect to={Routes.NotAuthorized} />;
            }

            return React.createElement(component as any, props);
            {}
          }}
          userData={loggedUser}
        />
      ))*/}

      <PrivateRoute path={Routes.NotAuthorized}>
        <NotAuthorizedPage />
      </PrivateRoute>
      
      <Route path="/error/message">
        <CustomErrorPage />
      </Route>

      <Route path="*">
        <GenericNotFoundPage />
      </Route>


    </Switch>
  );
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    menuStore: state.navigationMenuStore
  };
};

const mapDispatchToProps: IMapDispatchToProps = {
  refreshProfile
};

const ApplicationComponentWithRouter = withRouter(ApplicationRoutesComponent);

export const ApplicationRoutes = connect(mapStateToProps, mapDispatchToProps)(ApplicationComponentWithRouter);
